import { api } from '../../axios-config';
import { trackAdConversion, withAdTracking } from '@/lib/adPixels';
import type { AdConversion } from '@/utils/adTracking';

export const LOGIN_URL = `/auth/login`;
export const GET_USER_ME = `/users/me`;

export type Login = {
  email: string;
  password: string;
};

export type Tokens = {
  access: {
    token: string;
    expires: Date;
  };
  refresh: {
    token: string;
    expires: Date;
  };
};

export type LoginResponse = {
  user: UserResponse;
  tokens: Tokens;
};

export async function login(dataLogin: Login) {
  return await api.post<LoginResponse>(`${LOGIN_URL}`, dataLogin);
}

/** Login e cadastro pelo Google: o servidor valida o ID token e devolve a mesma sessão do login. */
export async function loginWithGoogle(credential: string) {
  const response = await api.post<LoginResponse & { created: boolean; adConversion?: AdConversion }>(
    `/auth/google`,
    { credential, ...withAdTracking() }
  );
  // Conta nova pelo Google: mesma conversão do cadastro por e-mail
  trackAdConversion(response.data.adConversion);
  return response.data;
}

export async function sendOtpByEmail(): Promise<{ expiresInSeconds?: number }> {
  const response = await api.post(`/auth/send-verification-email`);
  if (response.status !== 200) {
    throw new Error('Erro ao solicitar código!');
  }
  return response.data;
}

export type VerifyTokenSentToEmail = {
  otpToken: string;
  email: string;
};

/** Confirma o e-mail do usuário logado; devolve tokens novos da sessão. */
export async function verifyTokenSentToEmail(data: VerifyTokenSentToEmail): Promise<Tokens> {
  const response = await api.post(`/auth/verify-email`, data);
  if (response.status !== 200) {
    throw new Error('Erro ao verificar código!');
  }
  return response.data;
}

export type VerifyToken = {
  otpToken: string;
  email: string;
};

export const verifyToken = async ({ otpToken, email }: VerifyToken) => {
  const response = await api.post(`/auth/verify-otptoken`, { otpToken, email });
  return response;
};

export type ResetPassword = {
  token: string;
  password: string;
};

export const resetPassword = async ({ password, token }: ResetPassword) => {
  const response = await api.post(`/auth/reset-password`, { password, token });
  return response;
};

export type SendEmailForgotPassword = {
  email: string;
};

export type SendEmailForgotPasswordResponse = {
  email: string;
  /** Validade do código enviado, em segundos. */
  expiresInSeconds?: number;
};

export const sendEmailForgotPassword = async ({
  email,
}: SendEmailForgotPassword): Promise<SendEmailForgotPasswordResponse> => {
  const response = await api.post(`/auth/forgot-password`, { email });
  return response.data;
};

/** Prazo padrão do código caso o servidor não informe (OTP_EXPIRATION_MINUTES=2). */
const DEFAULT_OTP_SECONDS = 120;

export type OtpWindow = {
  /** Horário local (ms) em que o código expira. */
  deadline: number;
  totalSeconds: number;
};

/** Prazo do código, contado do momento em que a resposta do envio chegou. */
export const otpWindowFrom = (response: { expiresInSeconds?: number }): OtpWindow => {
  const totalSeconds = response.expiresInSeconds ?? DEFAULT_OTP_SECONDS;
  return { deadline: Date.now() + totalSeconds * 1000, totalSeconds };
};

export type UserResponse = {
  eventId: string;
  id: string;
  name: string;
  image: string;
  isForeign: boolean;
  cpf: string;
  email: string;
  role: string;
  isEmailVerified: boolean;
  isDeleted: boolean;
  createdAt: Date;
};

export async function getUserByToken() {
  return await api.post<UserResponse>(GET_USER_ME);
}
